import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type { LifeInsuranceDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { DetailDates } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailDates";
import { DetailLoader } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLoader";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { DetailValueChartSection } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailValueChartSection";
import { RelatedSectionRedesign } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedSectionRedesign";
import { FundListSection } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/FundListSection";
import { InsuredPersonsList } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/InsuredPersonsList";
import { PortfolioBasicInfoRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/PortfolioBasicInfoRows";
import { normalizeTools } from "web_myplann/Portfolio/Helpers/normalizeTools";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail;

export const LifeInsuranceRedesign: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailLoader />;
	}

	const portfolioData = baseData.data as LifeInsuranceDetailDataDTO;

	const insuredAmount = portfolioData.insurance
		? getPortfolioFrequencyValue(
				portfolioData.insurance ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const employersBonus = portfolioData.employerContribution
		? getPortfolioFrequencyValue(
				portfolioData.employerContribution ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	return (
		<>
			<DetailValueChartSection
				reporting={reporting}
				currencySign={currencySign}
			/>

			<DetailCard label={t(Resources.DataCard.BasicInfo.title)}>
				<PortfolioBasicInfoRows
					baseData={baseData}
					nameLabel={Resources.Common.FirstName}
				/>
				<DetailRow
					label={t(PageResources.LifeInsurance.insuredAmount)}
					value={insuredAmount}
				/>
				<DetailRow
					label={t(PageResources.LifeInsurance.employersBonus)}
					value={employersBonus}
				/>
			</DetailCard>
			<InsuredPersonsList
				insuredPersons={portfolioData.insuredPersons ?? []}
				currencySign={currencySign}
			/>
			<DetailDates data={baseData} />

			<FundListSection
				portfolioId={baseData.id}
				title={t(PageResources.LifeInsurance.InvestmentTool.title)}
				tools={normalizeTools(baseData.tools)}
				producerInstruments={baseData.producerInstruments}
				toolsSummary={reporting?.toolsSummary}
				currencySign={currencySign}
			/>

			<RelatedSectionRedesign
				documents={documents?.data}
				related={baseData.related}
				readOnly={baseData.archived}
				itemKind={PortfolioItemKind.Portfolio}
				portfolioId={baseData.id}
			/>
		</>
	);
};
